import React, { useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
    useUpdateWindowsQuery,
    useDeleteUpdateWindowMutation,
    useCreateUpdateWindowMutation,
    useUpdateUpdateWindowMutation
} from '../../hooks/useUpdateWindows';
import { UpdateWindow, CreateUpdateWindowData, EditUpdateWindowData } from '../../models/updateWindow';
import { useToast } from '../../hooks/useToast';
import { LoadingContainer } from '../../components/shared/LoadingContainer';
import UpdateWindowModal from '../../components/admin/UpdateWindowModal';

const UpdateWindowsPage: React.FC = () => {
    const { success, error } = useToast();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingWindow, setEditingWindow] = useState<UpdateWindow | null>(null);

    // Queries e mutações
    const { data: updateWindows = [], isLoading, error: queryError } = useUpdateWindowsQuery();
    const deleteMutation = useDeleteUpdateWindowMutation();
    const createMutation = useCreateUpdateWindowMutation();
    const updateMutation = useUpdateUpdateWindowMutation();

    const formatDate = (dateString: string) => {
        try {
            if (!dateString) return 'Data inválida';
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Data inválida';
            return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
        } catch (e) {
            return 'Data inválida';
        }
    };

    const formatPeriod = (startDate: string, endDate: string) => {
        try {
            if (!startDate || !endDate) return 'Período inválido';
            const start = new Date(startDate);
            const end = new Date(endDate);

            if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'Período inválido';

            return `${format(start, 'dd/MM/yyyy', { locale: ptBR })} - ${format(end, 'dd/MM/yyyy', { locale: ptBR })}`;
        } catch (e) {
            return 'Período inválido';
        }
    };

    const isActive = (window: UpdateWindow) => {
        if (!window.isActive) return false;
        const now = new Date();
        const start = new Date(window.startDate);
        const end = new Date(window.endDate);
        return now >= start && now <= end;
    };

    const getStatusBadge = (window: UpdateWindow) => {
        if (isActive(window)) {
            return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Ativa</span>;
        } else if (window.isActive) {
            const now = new Date();
            const start = new Date(window.startDate);
            if (now < start) {
                return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Agendada</span>;
            } else {
                return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Expirada</span>;
            }
        } else {
            return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Inativa</span>;
        }
    };

    const handleCreate = () => {
        setEditingWindow(null);
        setIsModalOpen(true);
    };

    const handleEdit = (window: UpdateWindow) => {
        setEditingWindow(window);
        setIsModalOpen(true);
    };

    const handleDelete = async (id: string, title: string) => {
        if (!window.confirm(`Tem certeza que deseja deletar a janela "${title}"?`)) {
            return;
        }

        try {
            await deleteMutation.mutateAsync(id);
            success('Janela deletada!', 'Janela de atualização removida com sucesso');
        } catch (err) {
            error('Erro ao deletar', 'Não foi possível deletar a janela de atualização');
        }
    };

    const handleSave = async (data: CreateUpdateWindowData | EditUpdateWindowData) => {
        try {
            if (editingWindow) {
                await updateMutation.mutateAsync({
                    id: editingWindow.id!,
                    data: data as EditUpdateWindowData
                });
                success('Janela atualizada!', 'Janela de atualização modificada com sucesso');
            } else {
                await createMutation.mutateAsync(data as CreateUpdateWindowData);
                success('Janela criada!', 'Nova janela de atualização criada com sucesso');
            }
            setIsModalOpen(false);
        } catch (err) {
            error('Erro ao salvar', 'Não foi possível salvar a janela de atualização');
        }
    };

    if (isLoading) {
        return <LoadingContainer message="Carregando janelas de atualização..." />;
    }

    if (queryError) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <h1 className="text-xl font-semibold text-red-600 mb-4">Erro ao carregar dados</h1>
                    <p className="text-gray-600">{queryError.message}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                                <CalendarDaysIcon className="w-8 h-8 text-indigo-600" />
                                Janelas de Atualização
                            </h1>
                            <p className="mt-2 text-gray-600">
                                Configure períodos em que colaboradores podem atualizar seus perfis
                            </p>
                        </div>
                        <button
                            onClick={handleCreate}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            <PlusIcon className="w-5 h-5 mr-2" />
                            Nova Janela
                        </button>
                    </div>
                </div>

                {/* Lista de janelas */}
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                    {updateWindows.length === 0 ? (
                        <div className="text-center py-12">
                            <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma janela configurada</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                Configure janelas de atualização para permitir que colaboradores atualizem seus perfis.
                            </p>
                            <div className="mt-6">
                                <button
                                    onClick={handleCreate}
                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    <PlusIcon className="w-5 h-5 mr-2" />
                                    Criar primeira janela
                                </button>
                            </div>
                        </div>
                    ) : (
                        <ul className="divide-y divide-gray-200">
                            {updateWindows.map((window) => (
                                <li key={window.id} className="p-6 hover:bg-gray-50">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3">
                                                <h3 className="text-lg font-medium text-gray-900">
                                                    {window.title}
                                                </h3>
                                                {getStatusBadge(window)}
                                                {window.isRequired && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                        Obrigatória
                                                    </span>
                                                )}
                                            </div>

                                            {window.description && (
                                                <p className="mt-1 text-sm text-gray-500">
                                                    {window.description}
                                                </p>
                                            )}

                                            <div className="mt-2 flex items-center text-sm text-gray-500 space-x-4">
                                                <span>
                                                    <strong>Período:</strong> {formatPeriod(window.startDate, window.endDate)}
                                                </span>
                                                <span>
                                                    <strong>Obrigatória:</strong> {window.isRequired ? 'Sim' : 'Não'}
                                                </span>
                                                <span>
                                                    <strong>Múltiplas atualizações:</strong> {window.allowMultipleUpdates ? 'Sim' : 'Não'}
                                                </span>
                                            </div>

                                            {window.createdAt && (
                                                <div className="mt-1 text-xs text-gray-400">
                                                    Criada em {formatDate(window.createdAt)}
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => handleEdit(window)}
                                                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            >
                                                <PencilIcon className="w-4 h-4 mr-1" />
                                                Editar
                                            </button>
                                            <button
                                                onClick={() => handleDelete(window.id!, window.title)}
                                                disabled={deleteMutation.isPending}
                                                className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                                            >
                                                <TrashIcon className="w-4 h-4 mr-1" />
                                                Deletar
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Modal */}
            <UpdateWindowModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSave}
                editingWindow={editingWindow}
                isLoading={createMutation.isPending || updateMutation.isPending}
            />
        </div>
    );
};

export default UpdateWindowsPage;